import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Layout.css";

/* ========== API BASE ========== */
const API_ORIGIN = (() => {
  const clean = (v) =>
    (v || "")
      .trim()
      .replace(/\/+$/, "")
      .replace(/\/api$/, "");

  const env = clean(process.env.REACT_APP_API_URL);
  if (env) return env;

  if (typeof window === "undefined") return "";

  const origin = window.location.origin || "";
  const isCapacitor = origin.startsWith("capacitor://");
  const isLocalhost = /localhost|127\.0\.0\.1/.test(origin);

  if (isCapacitor || isLocalhost) return "https://pay2pee.onrender.com";
  return "https://pay2pee.onrender.com";
})();

const API_BASE = `${API_ORIGIN}/api`;

/* ========== AVATAR ========== */
function InitialsAvatar({ name = "User", className = "guest-nav-pic" }) {
  const initials = useMemo(() => {
    const parts = String(name).trim().split(/\s+/);
    return ((parts[0]?.[0] || "U") + (parts[1]?.[0] || "")).toUpperCase();
  }, [name]);

  return (
    <div
      className={className}
      title={name}
      style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #2563eb, #f59e0b)",
        color: "#fff",
        fontWeight: 800,
        fontSize: 14
      }}
    >
      {initials}
    </div>
  );
}

/* ========== ICONS (CLEAN SVG) ========== */

const HomeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M3 10.5L12 3l9 7.5V21a1 1 0 01-1 1h-5v-7h-6v7H4a1 1 0 01-1-1v-10.5z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AnalyticsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 19V10M10 19V5M16 19v-8M22 19H2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MenuIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="6" r="2" fill="white" />
    <circle cx="12" cy="12" r="2" fill="white" />
    <circle cx="12" cy="18" r="2" fill="white" />
  </svg>
);

/* ========== LAYOUT ========== */

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  if (location.pathname === "/host-landing") return <>{children}</>;

  const isAuthPage = location.pathname === "/auth" || location.pathname === "/";
  const isPartnerPage = location.pathname.startsWith("/partner");

  const [user, setUser] = useState(() => ({
    token: localStorage.getItem("token"),
    userType: localStorage.getItem("userType"),
    name: localStorage.getItem("userName") || "Guest",
    avatarUrl: localStorage.getItem("avatarUrl") || ""
  }));

  const [lastSessionId, setLastSessionId] = useState(() =>
    typeof window === "undefined" ? null : localStorage.getItem("lastSessionId")
  );

  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const update = () =>
      setUser({
        token: localStorage.getItem("token"),
        userType: localStorage.getItem("userType"),
        name: localStorage.getItem("userName") || "Guest",
        avatarUrl: localStorage.getItem("avatarUrl") || ""
      });

    window.addEventListener("storage", update);
    window.addEventListener("auth:update", update);
    return () => {
      window.removeEventListener("storage", update);
      window.removeEventListener("auth:update", update);
    };
  }, []);

  useEffect(() => {
    const id = localStorage.getItem("lastSessionId");
    setLastSessionId(id);
  }, []);

  const isLoggedIn = Boolean(user.token);
  const isPartner = user.userType === "partner";

  useEffect(() => {
    if (!isLoggedIn) return;
    const path = location.pathname;

    if (isPartner && path.startsWith("/guest")) navigate("/partner", { replace: true });
    if (!isPartner && path.startsWith("/partner")) navigate("/home", { replace: true });
  }, [isLoggedIn, isPartner, location.pathname, navigate]);

  const signOut = () => {
    ["token", "userId", "userType", "userName", "avatarUrl"].forEach((k) =>
      localStorage.removeItem(k)
    );
    window.dispatchEvent(new Event("auth:update"));
    setMenuOpen(false);
    navigate("/");
  };

  // ✅ NEW
  const deleteAccount = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to delete your account? This cannot be undone."
    );
    if (!confirmed) return;

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      const res = await fetch(`${API_BASE}/users/account`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        alert(data.error || "Failed to delete account.");
        return;
      }

      ["token", "userId", "userType", "userName", "avatarUrl"].forEach((k) =>
        localStorage.removeItem(k)
      );

      window.dispatchEvent(new Event("auth:update"));
      setMenuOpen(false);
      navigate("/", { replace: true });
    } catch (err) {
      console.error("Delete account error:", err);
      alert("Failed to delete account.");
    }
  };

  const AvatarImg = ({ name, url, className }) =>
    url ? (
      <img
        src={url}
        alt={name}
        className={className}
        style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }}
        onError={(e) => (e.currentTarget.style.display = "none")}
      />
    ) : (
      <InitialsAvatar name={name} className={className} />
    );

  return (
    <div className="p2p-app-root">
      <div className="p2p-app-shell">
        <div className="app-container">
          {isLoggedIn && !isAuthPage && (
            <header className="app-header">
              <div className="header-content">
                {/* NAV */}
                <nav className="app-nav">
                  {!isPartner && (
                    <>
                      <Link to="/home" className="nav-link home-link">
                        <HomeIcon />
                      </Link>

                      <Link to="/guest" className="nav-link profile-link" title={user.name}>
                        <AvatarImg name={user.name} url={user.avatarUrl} className="guest-nav-pic" />
                      </Link>
                    </>
                  )}

                  {isPartner && (
                    <>
                      <Link to="/partner/analytics" className="nav-link analytics-link">
                        <AnalyticsIcon />
                      </Link>

                      <Link to="/partner" className="nav-link profile-link" title={user.name}>
                        <AvatarImg name={user.name} url={user.avatarUrl} className="partner-nav-pic" />
                      </Link>
                    </>
                  )}

                  {/* MENU */}
                  <div className="nav-menu-wrapper">
                    <button
                      type="button"
                      className="nav-menu-button"
                      onClick={() => setMenuOpen((prev) => !prev)}
                    >
                      <MenuIcon />
                    </button>

                    {menuOpen && (
                      <div className="nav-menu-dropdown">
                        {!isPartner && lastSessionId && (
                          <Link to={`/mypass?session_id=${lastSessionId}`} className="nav-menu-item">
                            🎟 My Pass
                          </Link>
                        )}

                        <Link to="/notifications" className="nav-menu-item">
                          🔔 Notifications
                        </Link>
                        <Link to="/chat" className="nav-menu-item">
                          💬 Messages
                        </Link>

                        <hr />

                        <Link to="/privacy" className="nav-menu-item">
                          Privacy Policy
                        </Link>
                        <Link to="/terms" className="nav-menu-item">
                          Terms of Service
                        </Link>
                        <Link to="/eula" className="nav-menu-item">
                          EULA
                        </Link>
                        <Link to="/refunds" className="nav-menu-item">
                          Refunds
                        </Link>

                        {/* ✅ NEW */}
                        <button onClick={deleteAccount} className="nav-menu-item danger">
                          Delete Account
                        </button>

                        <button onClick={signOut} className="nav-menu-item danger">
                          Sign out
                        </button>
                      </div>
                    )}
                  </div>
                </nav>
              </div>
            </header>
          )}

          <main className="app-main">{children}</main>
          <footer className="app-footer" />
        </div>
      </div>
    </div>
  );
}